type TeamSeatPricing = { [x: string]:number }

// TODO: generate this from actual backend api
export type RenderedPlanApi = {
  active:boolean
  'analytics_sub_category'?:string
  'content_type':string
  'created_at':string
  'credit_type':string
  credits:number
  currency:string
  'description_title':string
  frequencey:number
  'friendly_name':string
  'friendly_name_with_price':string
  interval:string
  'merchant_account_id':string
  'seats_num'?:number
  period:string
  'plan_id'?:string
  'plan_type':string
  'price_in_cents':number
  recurring:boolean
  'remote_id':string
  sku:string
  subtitle:string
  'team_seat_pricing':TeamSeatPricing
  title:string
  'unlimited_credits':boolean
}

export type PlanItem = {
  id:string
  name:string
  brand:string
  category:string
  variant:string
  price:number
  quantity:number
}

function teamAndSeats({ plan, seatsNum }:{ plan:RenderedPlanApi, seatsNum:number }):boolean {
  return plan.team_seat_pricing && seatsNum && seatsNum > 1;
}

export const planInterval = (plan:RenderedPlanApi):string => (plan.interval === 'A' ? 'Annual' : 'Monthly');

export const isSubscriptionPlan = (plan:RenderedPlanApi):boolean => plan.plan_type === 'subscription' && plan.recurring === true;

export const transactionBrand = (plan:RenderedPlanApi):string => (plan.plan_type === 'subscription' ? 'Subscription' : 'One-Off');

export const transactionCategory = ({ plan, seatsNum = null }: { plan:RenderedPlanApi, seatsNum:number }):string => {
  const subPlanCategory = teamAndSeats({ plan, seatsNum }) ? `Teams-${planInterval(plan)}` : planInterval(plan);

  if (isSubscriptionPlan(plan)) return subPlanCategory;

  // matches custom-design or custom_design case insensitive
  if (/custom(-|_){1}design/i.test(plan.remote_id)) return 'Credits-Design';

  return `Credits-${plan.credits}`;
};

export const calcSeatPrice = ({
  plan,
  seatsNum,
  interval,
}:{ plan:RenderedPlanApi, seatsNum:number, interval:string }):number => {
  if (!teamAndSeats({ plan, seatsNum })) return null;

  const seatsPricing = plan.team_seat_pricing;

  const seatsNumKeysMap = Object.keys(seatsPricing).map((num) => parseInt(num, 10));

  // If the selected number of seats isn't in the pricing object,
  // then just return the price for the highest number of seats available
  let seatPrice = seatsPricing[seatsNum - 1] || seatsPricing[seatsNumKeysMap.length.toString()];

  if (interval === 'annual') {
    seatPrice /= 12;
  }

  return seatPrice / 100;
};

export const calcTeamsPriceTotal = ({
  basePlanPrice,
  plan,
  seatsNum,
  interval,
}:{ basePlanPrice:number, plan:RenderedPlanApi, seatsNum:number, interval:string }):number => {
  const seatPrice = calcSeatPrice({ plan, seatsNum, interval });

  return basePlanPrice + ((seatsNum - 1) * seatPrice);
};

export const transactionAmount = ({ plan, seatsNum = null }:{ plan:RenderedPlanApi, seatsNum?:number }):number => {
  let priceInDollars = plan.price_in_cents / 100;

  if (teamAndSeats({ plan, seatsNum })) {
    priceInDollars = calcTeamsPriceTotal({
      basePlanPrice: priceInDollars,
      plan,
      seatsNum,
      interval: planInterval(plan),
    });
  }

  return priceInDollars;
};

export const buildPlanItems = ({ plans, seatsNum = null }:{ plans:RenderedPlanApi[], seatsNum?:number }):PlanItem[] => {
  const plansArray = Array.isArray(plans) ? plans : [plans];

  return plansArray.map((plan:RenderedPlanApi):PlanItem => ({
    id: plan.remote_id,
    name: plan.title,
    brand: transactionBrand(plan),
    category: transactionCategory({ plan, seatsNum: seatsNum || plan.seats_num }),
    variant: plan.content_type,
    price: transactionAmount({ plan, seatsNum: seatsNum || plan.seats_num }),
    quantity: 1,
  }));
};
